import { trackSignUp } from "@/lib/analytics";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import TermsAcceptanceCheckbox from "@/components/TermsAcceptanceCheckbox";
import { supabase } from "@/integrations/supabase/client";
import { markFreshLogin } from "@/lib/fresh-login-signal";
import { socialLogin } from "@/lib/social-auth";
import { setPostAuthIntent } from "@/lib/post-auth-intent";
import { getPanelUrl } from "@/lib/panel-url";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Guest diagnosis session id — propagated through OAuth so the panel can claim & import after sign-in. */
  sessionId?: string;
}

type View = "loading" | "teaser" | "register" | "login";

const LOADING_MS = 2500;
const STEP_MS = 800;

const DiagnosisAuthFlow = ({ open, onOpenChange, sessionId }: Props) => {
  const { t, lang } = useLanguage();
  const [view, setView] = useState<View>("loading");
  const [stepIdx, setStepIdx] = useState(0);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);

  useEffect(() => {
    if (open) {
      setView("loading");
      setStepIdx(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || view !== "loading") return;
    const interval = setInterval(() => setStepIdx((i) => (i + 1) % 3), STEP_MS);
    const timeout = setTimeout(() => setView("teaser"), LOADING_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [open, view]);

  // Build target URL carrying the guest session id, so the panel restores it
  // even when localStorage didn't survive a cross-host OAuth hop.
  const targetPath = sessionId
    ? `/dashboard?tab=locations&claim_loc_session=${encodeURIComponent(sessionId)}`
    : "/dashboard?tab=locations";

  // Persist intent (→ Mis Locales tab) + auto-close on auth
  useEffect(() => {
    if (!open) return;
    setPostAuthIntent({ kind: "path", path: targetPath });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === "SIGNED_IN" || event === "INITIAL_SESSION")) {
        onOpenChange(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [open, onOpenChange, targetPath]);

  const loadingTexts = [
    t("locationDiagnosis", "analysisLoading1"),
    t("locationDiagnosis", "analysisLoading2"),
    t("locationDiagnosis", "analysisLoading3"),
  ];

  const goToPanel = () => {
    window.location.assign(getPanelUrl(targetPath));
  };

  const handleGoogle = async () => {
    try {
      await socialLogin("google", { kind: "path", path: targetPath });
    } catch {
      toast.error(t("auth", "errorGoogle"));
    }
  };

  const handleSubmit = async () => {
    if (!email || !password) {
      toast.error(t("auth", "fillFields"));
      return;
    }
    setLoading(true);
    try {
      if (view === "login") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) { toast.error(error.message); return; }
        markFreshLogin();
        toast.success(t("auth", "welcomeBack"));
      } else {
        if (password.length < 6) { toast.error(t("auth", "passwordMin")); return; }
        if (!tosAccepted) {
          toast.error(lang === "es"
            ? "Debes aceptar los Términos de Servicio."
            : "You must accept the Terms of Service.");
          return;
        }
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.hostname.includes("retaillemon.com") ? "https://panel.retaillemon.com" : window.location.origin}/`,
            data: { first_name: firstName, last_name: lastName },
          },
        });
        if (error) { toast.error(error.message); return; }
        if (data.user) trackSignUp(data.user.id, "email");
        if (data.session) markFreshLogin();
        toast.success(t("auth", "accountCreated"));
      }
      onOpenChange(false);
      goToPanel();
    } finally {
      setLoading(false);
    }
  };

  const isForm = view === "register" || view === "login";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden border-border">
        <VisuallyHidden>
          <DialogTitle>{t("locationDiagnosis", "teaserTitle")}</DialogTitle>
          <DialogDescription>{t("locationDiagnosis", "teaserSubtitle")}</DialogDescription>
        </VisuallyHidden>

        <motion.div layout transition={{ duration: 0.3, ease: "easeInOut" }}>
          <AnimatePresence mode="wait" initial={false}>
            {view === "loading" && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="p-8 flex flex-col items-center text-center"
              >
                <Loader2 className="h-10 w-10 text-foreground animate-spin mb-5" strokeWidth={1.75} />
                <AnimatePresence mode="wait">
                  <motion.p
                    key={stepIdx}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.25 }}
                    className="font-body text-sm text-muted-foreground min-h-[1.5rem]"
                  >
                    {loadingTexts[stepIdx]}
                  </motion.p>
                </AnimatePresence>
              </motion.div>
            )}

            {view === "teaser" && (
              <motion.div
                key="teaser"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="p-7 flex flex-col items-center text-center"
              >
                <div className="text-4xl mb-3" aria-hidden="true">🍋</div>
                <h2 className="font-display text-xl font-bold mb-2">
                  {t("locationDiagnosis", "teaserTitle")}
                </h2>
                <p className="font-body text-sm text-muted-foreground mb-6">
                  {t("locationDiagnosis", "teaserSubtitle")}
                </p>
                <div className="w-full flex flex-col gap-2">
                  <Button
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold min-h-11"
                    onClick={() => setView("register")}
                  >
                    {t("locationDiagnosis", "teaserCtaSignup")}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full min-h-11"
                    onClick={() => setView("login")}
                  >
                    {t("locationDiagnosis", "teaserCtaLogin")}
                  </Button>
                </div>
              </motion.div>
            )}

            {isForm && (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="p-6"
              >
                <button
                  type="button"
                  onClick={() => setView("teaser")}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-3"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t("locationDiagnosis", "backToTeaser").replace("← ", "")}
                </button>

                <h2 className="font-display text-2xl font-bold text-center mb-5">
                  {view === "register"
                    ? t("locationDiagnosis", "viewRegisterTitle")
                    : t("locationDiagnosis", "viewLoginTitle")}
                </h2>

                <Button
                  variant="outline"
                  size="lg"
                  className="w-full gap-3 font-semibold border-border mb-4"
                  onClick={handleGoogle}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t("auth", "continueGoogle")}
                </Button>

                <div className="flex items-center gap-3 mb-4">
                  <Separator className="flex-1" />
                  <span className="font-body text-xs text-muted-foreground whitespace-nowrap">
                    {t("auth", "orEmail")}
                  </span>
                  <Separator className="flex-1" />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={view}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-3"
                  >
                    {view === "register" && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="df-first" className="font-body text-xs">{t("auth", "firstName")}</Label>
                          <Input id="df-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="María" className="mt-1" />
                        </div>
                        <div>
                          <Label htmlFor="df-last" className="font-body text-xs">{t("auth", "lastName")}</Label>
                          <Input id="df-last" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="García" className="mt-1" />
                        </div>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="df-email" className="font-body text-xs">{t("auth", "email")}</Label>
                      <Input id="df-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="df-password" className="font-body text-xs">{t("auth", "password")}</Label>
                      <div className="relative mt-1">
                        <Input id="df-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••" className="pr-10" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    {view === "register" && (
                      <TermsAcceptanceCheckbox id="df-tos" checked={tosAccepted} onCheckedChange={setTosAccepted} />
                    )}
                    <Button
                      size="lg"
                      className="w-full font-semibold"
                      onClick={handleSubmit}
                      disabled={loading || (view === "register" && !tosAccepted)}
                    >
                      {loading
                        ? t("auth", "loadingBtn")
                        : view === "register"
                          ? t("auth", "signupBtn")
                          : t("auth", "loginBtn")}
                    </Button>
                  </motion.div>
                </AnimatePresence>

                <p className="text-center font-body text-sm text-muted-foreground mt-4">
                  {view === "register" ? (
                    <>
                      {t("locationDiagnosis", "switchToLogin")}{" "}
                      <button onClick={() => setView("login")} className="font-semibold text-foreground hover:text-foreground transition-colors">
                        {t("locationDiagnosis", "switchToLoginLink")}
                      </button>
                    </>
                  ) : (
                    <>
                      {t("locationDiagnosis", "switchToRegister")}{" "}
                      <button onClick={() => setView("register")} className="font-semibold text-foreground hover:text-foreground transition-colors">
                        {t("locationDiagnosis", "switchToRegisterLink")}
                      </button>
                    </>
                  )}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default DiagnosisAuthFlow;
